import { Injectable } from '@angular/core'
import { HttpEventType } from '@angular/common/http'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, delay, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { AppBasicWorkpackage } from '../../models/interfaces/app-basic-workpackage.interface'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { AppFileDownloadService } from '../../services/app-file-download.service'
import { getProject } from '../stage/stage.selectors'
import { AppState } from '../storage'
import { AppNotificationService } from '../../services/app-notification.service'
import { AppSpinnerService } from '../../services/app-spinner.service'
import { AppCoreService } from '../../services/app-core.service'
import * as LumpSumActions from './lump-sum.actions'
import { LumpSumService } from './lump-sum.service'

@Injectable()
export class LumpSumEffects {
   loadWorkpackages$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_Workpackages),
         switchMap(() =>
            this.lumpSumService.get_LS_WorkpackageList().pipe(
               map((response: AppBasicWorkpackage[]) => LumpSumActions.load_LS_WorkpackagesSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_WorkpackagesFailure({ error })))
            )
         )
      )
   )

   // summary for wp

   loadSummaryForWpData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_SummaryForWpData),
         switchMap((action) =>
            this.lumpSumService.get_LS_SummaryForWpData(action.workPackageId).pipe(
               map((response) => LumpSumActions.load_LS_SummaryForWpDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_SummaryForWpDataFailure({ error })))
            )
         )
      )
   )

   // beneficiaries list
   loadBeneficiariesList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_BeneficiariesList),
         switchMap(() =>
            this.lumpSumService.get_LS_BeneficiariesList().pipe(
               map((response) => LumpSumActions.load_LS_BeneficiariesListSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_BeneficiariesListFailure({ error })))
            )
         )
      )
   )

   // costs per wp
   loadCostsPerWpBeneficiarySummaryTableData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_CostsPerWpBeneficiarySummaryTableData),
         switchMap((action) =>
            this.lumpSumService.get_LS_SummaryForBeneficiaryData(action.beneficiaryId).pipe(
               map((response) => LumpSumActions.load_LS_CostsPerWpBeneficiarySummaryTableDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_CostsPerWpBeneficiarySummaryTableDataFailure({ error })))
            )
         )
      )
   )

   // costs per wp
   loadCostsPerWpSingleWpTableData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_CostsPerWpSingleWpTableData),
         switchMap((action) =>
            this.lumpSumService.get_LS_CostsPerWpSingleWpTableData(action.beneficiaryId, action.workPackageId).pipe(
               map((response) => LumpSumActions.load_LS_CostsPerWpSingleWpTableDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_CostsPerWpSingleWpTableDataFailure({ error })))
            )
         )
      )
   )

   updateCostsPerWpSingleWpTableData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.update_LS_CostsPerWpSingleWpTableData),
         switchMap((action) =>
            this.lumpSumService.update_LS_CostsPerWpSingleWpTableData(action.beneficiaryId, action.workPackageId, action.requestBody).pipe(
               map(() => LumpSumActions.update_LS_CostsPerWpSingleWpTableDataSuccess()),
               catchError((error) => of(LumpSumActions.update_LS_CostsPerWpSingleWpTableDataFailure({ error })))
            )
         )
      )
   )

   // ESTIMATED BREAKDOWN
   loadEstimatedBreakdownData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_EstimatedBreakdownData),
         switchMap(() =>
            this.lumpSumService.get_LS_EstimatedBreakdownData().pipe(
               map((response) => LumpSumActions.load_LS_EstimatedBreakdownDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_EstimatedBreakdownDataFailure({ error })))
            )
         )
      )
   )

   // PERSON MONTHS
   loadPersonMonthsData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_PersonMonthsData),
         switchMap(() =>
            this.lumpSumService.get_LS_PersonMonthsData().pipe(
               map((response) => LumpSumActions.load_LS_PersonMonthsDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_PersonMonthsDataFailure({ error })))
            )
         )
      )
   )

   // DEPRECIATION
   loadDepreciationData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_DepreciationData),
         switchMap((action) =>
            this.lumpSumService.get_LS_DepreciationData(action.beneficiaryId, action.workPackageId).pipe(
               map((response) => LumpSumActions.load_LS_DepreciationDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_DepreciationDataFailure({ error })))
            )
         )
      )
   )

   updateDepreciationData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.update_LS_DepreciationData),
         switchMap((action) =>
            this.lumpSumService.update_LS_DepreciationData(action.beneficiaryId, action.workPackageId, action.requestBody).pipe(
               map(() => LumpSumActions.update_LS_DepreciationDataSuccess()),
               catchError((error) => of(LumpSumActions.update_LS_DepreciationDataFailure({ error })))
            )
         )
      )
   )

   // resource types
   loadResourceTypes$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_ResourceTypes),
         switchMap(() =>
            this.lumpSumService.get_LS_ResourceTypes().pipe(
               map((response) => LumpSumActions.load_LS_ResourceTypesSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_ResourceTypesFailure({ error })))
            )
         )
      )
   )

   // any comments
   loadAnyCommentsData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_AnyCommentsData),
         switchMap((action) =>
            this.lumpSumService.get_LS_AnyComments(action.beneficiaryId, action.workPackageId).pipe(
               map((response) => LumpSumActions.load_LS_AnyCommentsDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_AnyCommentsDataFailure({ error })))
            )
         )
      )
   )

   updateAnyCommentsData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.update_LS_AnyCommentsData),
         switchMap((action) =>
            this.lumpSumService.update_LS_AnyComments(action.beneficiaryId, action.workPackageId, action.requestBody).pipe(
               map(() => LumpSumActions.update_LS_AnyCommentsDataSuccess()),
               catchError((error) => of(LumpSumActions.update_LS_AnyCommentsDataFailure({ error })))
            )
         )
      )
   )

   // change log
   loadChangeHistoryData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.load_LS_ChangeHistoryData),
         switchMap((action) =>
            this.lumpSumService.get_LS_ChangeHistory().pipe(
               map((response) => LumpSumActions.load_LS_ChangeHistoryDataSuccess({ response })),
               catchError((error) => of(LumpSumActions.load_LS_ChangeHistoryDataFailure({ error })))
            )
         )
      )
   )

   updateChangeHistoryData$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.update_LS_ChangeHistoryData),
         switchMap((action) =>
            this.lumpSumService.update_LS_ChangeHistory(action.requestBody).pipe(
               map(() => LumpSumActions.update_LS_ChangeHistoryDataSuccess()),
               catchError((error) => of(LumpSumActions.update_LS_ChangeHistoryDataFailure({ error })))
            )
         )
      )
   )

   // download lumpsum excel
   downloadLumpSum$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.downloadLumpSum),
         switchMap(() =>
            this.lumpSumService.exportLumpSum().pipe(
               map((response) => LumpSumActions.downloadLumpSumSuccess({ response })),
               catchError((error) => of(LumpSumActions.downloadLumpSumFailure({ error })))
            )
         )
      )
   )

   saveLumpSumFile$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(LumpSumActions.downloadLumpSumSuccess),
            withLatestFrom(this.store.select(getProject)),
            tap(([action, project]) => this.fileDownloadService.saveProject(action.response, project))
         ),
      { dispatch: false }
   )

   closeExportProgress$ = createEffect(() =>
      this.actions$.pipe(
         ofType(LumpSumActions.downloadLumpSumSuccess),
         filter(({ response }) => response.type === HttpEventType.Response),
         delay(1000),
         map(() => LumpSumActions.closeExportProgress())
      )
   )

   constructor(
      private actions$: Actions,
      private lumpSumService: LumpSumService,
      private store: Store<AppState>,
      private coreService: AppCoreService,
      private spinnerService: AppSpinnerService,
      private notificationService: AppNotificationService,
      private errorHandlerService: AppErrorHandlerService,
      private fileDownloadService: AppFileDownloadService
   ) {}

   private _actionsForSpinnerHide = []

   listenAllSpinnerHideAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerHide),
            map(() => this.spinnerService.spinnerHide())
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerShow = []

   listenAllSpinnerShowAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerShow),
            map(() => this.spinnerService.spinnerShow())
         ),
      { dispatch: false }
   )

   private _actionsFailedNotification = [
      LumpSumActions.load_LS_WorkpackagesFailure,
      LumpSumActions.load_LS_SummaryForWpDataFailure,
      LumpSumActions.load_LS_BeneficiariesListFailure,
      LumpSumActions.load_LS_CostsPerWpBeneficiarySummaryTableDataFailure,
      LumpSumActions.load_LS_CostsPerWpSingleWpTableDataFailure,
      LumpSumActions.update_LS_CostsPerWpSingleWpTableDataFailure,
      LumpSumActions.load_LS_EstimatedBreakdownDataFailure,
      LumpSumActions.load_LS_PersonMonthsDataFailure,
      LumpSumActions.load_LS_DepreciationDataFailure,
      LumpSumActions.update_LS_DepreciationDataFailure,
      LumpSumActions.load_LS_ResourceTypesFailure,
      LumpSumActions.load_LS_AnyCommentsDataFailure,
      LumpSumActions.update_LS_AnyCommentsDataFailure,
      LumpSumActions.load_LS_ChangeHistoryDataFailure,
      LumpSumActions.update_LS_ChangeHistoryDataFailure,
      LumpSumActions.downloadLumpSumFailure]

   listenAllFailedAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => {
               this.errorHandlerService.handleError(errorResponse.error)
            })
         ),
      { dispatch: false }
   )

   private _actionsSuccessNotification = [
      LumpSumActions.update_LS_CostsPerWpSingleWpTableDataSuccess,
      LumpSumActions.update_LS_DepreciationDataSuccess,
      LumpSumActions.update_LS_AnyCommentsDataSuccess,
      LumpSumActions.update_LS_ChangeHistoryDataSuccess]

   listenAllSuccessAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsSuccessNotification),
            map(() => {
               this.notificationService.notify(NotificationType.Success)
               this.coreService.closeDialog()
            })
         ),
      { dispatch: false }
   )
}
